import { useEffect } from 'react'

interface SEOProps {
  title: string
  description?: string
  image?: string
  keywords?: string[]
  canonicalUrl?: string
  themeColor?: string
  structuredData?: Record<string, unknown>
}

export function SEO({
  title,
  description,
  image,
  keywords,
  canonicalUrl,
  themeColor,
  structuredData,
}: SEOProps) {
  useEffect(() => {
    // Update main title
    document.title = title

    // Update meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords?.join(', ') },
      { name: 'theme-color', content: themeColor },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
    ]

    metaTags.forEach(({ name, property, content }) => {
      if (content) {
        let element = document.querySelector(
          `meta[${name ? `name="${name}"` : `property="${property}"`}]`,
        )

        if (!element) {
          element = document.createElement('meta')
          if (name) element.setAttribute('name', name)
          if (property) element.setAttribute('property', property)
          document.head.appendChild(element)
        }

        element.setAttribute('content', content)
      }
    })

    // Update Canonical URL
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]')
      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        document.head.appendChild(link)
      }
      link.setAttribute('href', canonicalUrl)
    }

    // Inject Structured Data (JSON-LD)
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]')
      if (!script) {
        script = document.createElement('script')
        script.setAttribute('type', 'application/ld+json')
        document.head.appendChild(script)
      }
      script.textContent = JSON.stringify(structuredData)
    }
  }, [title, description, image, keywords, canonicalUrl, themeColor, structuredData])

  return null
}
